import { Combobox } from '@base-ui/react/combobox'
import { Check, ChevronsUpDown, LoaderCircle, X } from 'lucide-react'
import * as React from 'react'
import { cn } from '../lib/cn'

export interface PickerOption {
  value: string
  label: string
  description?: string
  /** @deprecated Use description. Kept for SearchableSelect compatibility. */
  detail?: string
  group?: string
  keywords?: string[]
  current?: boolean
  disabled?: boolean
}

type LoadPickerOptions = (query: string, signal: AbortSignal) => Promise<PickerOption[]>

interface PickerBaseProps {
  options?: PickerOption[]
  ariaLabel?: string
  'aria-label'?: string
  placeholder?: string
  disabled?: boolean
  required?: boolean
  name?: string
  id?: string
  title?: string
  className?: string
  autoFocus?: boolean
  clearable?: boolean
  clearLabel?: string
  emptyMessage?: string
  loadingMessage?: string
  errorMessage?: string
  loadOptions?: LoadPickerOptions
}

export interface PickerSingleProps extends PickerBaseProps {
  multiple?: false
  value: string
  onValueChange?: (value: string) => void
  /** Compatibility bridge for replacing native selects without changing form logic. */
  onChange?: React.ChangeEventHandler<HTMLSelectElement>
  children?: React.ReactNode
}

export interface PickerMultipleProps extends PickerBaseProps {
  multiple: true
  value: string[]
  onValueChange: (value: string[]) => void
  children?: never
  onChange?: never
}

export type PickerProps = PickerSingleProps | PickerMultipleProps

interface PickerGroup {
  [key: string]: unknown
  value: string
  items: PickerOption[]
}

function revealPickerField(field: HTMLElement | null) {
  if (!field || typeof window === 'undefined' || !window.matchMedia?.('(max-width: 899px)').matches) return
  requestAnimationFrame(() => field.scrollIntoView({ block: 'start' }))
}

function optionText(option: PickerOption) {
  return [option.label, option.description, option.detail, ...(option.keywords ?? [])].filter(Boolean).join(' ')
}

function groupOptions(options: PickerOption[]): PickerGroup[] {
  const groups: PickerGroup[] = []
  const byName = new Map<string, PickerGroup>()
  for (const option of options) {
    const name = option.group ?? ''
    let group = byName.get(name)
    if (!group) {
      group = { value: name, items: [] }
      groups.push(group)
      byName.set(name, group)
    }
    group.items.push(option)
  }
  return groups
}

function textOf(node: React.ReactNode): string {
  if (typeof node === 'string' || typeof node === 'number') return String(node)
  if (Array.isArray(node)) return node.map(textOf).join('')
  if (React.isValidElement<{ children?: React.ReactNode }>(node)) return textOf(node.props.children)
  return ''
}

function optionsFromChildren(children: React.ReactNode, inheritedGroup?: string): PickerOption[] {
  const result: PickerOption[] = []
  React.Children.forEach(children, (child) => {
    if (!React.isValidElement(child)) return
    if (child.type === React.Fragment) {
      result.push(...optionsFromChildren((child.props as { children?: React.ReactNode }).children, inheritedGroup))
      return
    }
    if (child.type === 'optgroup') {
      const props = child.props as React.OptgroupHTMLAttributes<HTMLOptGroupElement>
      result.push(...optionsFromChildren(props.children, props.label))
      return
    }
    if (child.type !== 'option') return
    const props = child.props as React.OptionHTMLAttributes<HTMLOptionElement> & {
      'data-description'?: string
      'data-current'?: boolean | 'true'
      'data-keywords'?: string
    }
    result.push({
      value: String(props.value ?? textOf(props.children)),
      label: textOf(props.children),
      description: props['data-description'] ?? props.title,
      group: inheritedGroup,
      current: props['data-current'] === true || props['data-current'] === 'true',
      keywords: props['data-keywords']?.split(/\s+/).filter(Boolean),
      disabled: props.disabled,
    })
  })
  return result
}

function mergeOptions(primary: PickerOption[], secondary: PickerOption[]) {
  const merged = [...primary]
  const seen = new Set(primary.map((option) => option.value))
  for (const option of secondary) {
    if (!seen.has(option.value)) {
      merged.push(option)
      seen.add(option.value)
    }
  }
  return merged
}

function useAsyncOptions(options: PickerOption[], loadOptions?: LoadPickerOptions) {
  const [query, setQuery] = React.useState('')
  const [results, setResults] = React.useState<PickerOption[]>(options)
  const [knownOptions, setKnownOptions] = React.useState<PickerOption[]>(options)
  const [loading, setLoading] = React.useState(false)
  const [failed, setFailed] = React.useState(false)
  const loaderRef = React.useRef(loadOptions)
  const hasLoader = Boolean(loadOptions)
  loaderRef.current = loadOptions

  React.useEffect(() => {
    setKnownOptions((known) => mergeOptions(options, known))
  }, [options])

  React.useEffect(() => {
    if (!hasLoader) return
    const controller = new AbortController()
    const timer = window.setTimeout(() => {
      setLoading(true)
      setFailed(false)
      void loaderRef
        .current?.(query, controller.signal)
        .then((next) => {
          if (!controller.signal.aborted) {
            setResults(next)
            setKnownOptions((known) => mergeOptions(known, next))
          }
        })
        .catch(() => {
          if (!controller.signal.aborted) {
            setResults([])
            setFailed(true)
          }
        })
        .finally(() => {
          if (!controller.signal.aborted) setLoading(false)
        })
    }, 150)
    return () => {
      window.clearTimeout(timer)
      controller.abort()
    }
  }, [hasLoader, query])

  return {
    setQuery,
    results: loadOptions ? results : options,
    knownOptions: loadOptions ? knownOptions : options,
    loading,
    failed,
  }
}

function Item({ option }: { option: PickerOption }) {
  const description = option.description ?? option.detail
  return (
    <Combobox.Item
      value={option.value}
      data-value={option.value}
      disabled={option.disabled}
      data-slot="picker-item"
      className={cn(
        'flex cursor-default items-center gap-2 rounded-md px-2 py-2 text-sm outline-none select-none',
        'max-[899px]:min-h-11',
        'data-[highlighted]:bg-accent data-[highlighted]:text-accent-foreground data-[disabled]:opacity-50',
      )}
    >
      <span className="min-w-0 flex-1">
        <span className="flex items-center gap-2">
          <span className="truncate">{option.label}</span>
          {option.current ? (
            <span className="shrink-0 rounded-full bg-muted px-1.5 py-0.5 text-[10px] font-medium text-muted-foreground">Current</span>
          ) : null}
        </span>
        {description ? <span className="block truncate text-xs text-muted-foreground">{description}</span> : null}
      </span>
      <Combobox.ItemIndicator className="shrink-0 text-primary">
        <Check aria-hidden className="size-4" />
      </Combobox.ItemIndicator>
    </Combobox.Item>
  )
}

function List({
  loading,
  failed,
  loadingMessage,
  errorMessage,
  emptyMessage,
  onDone,
}: {
  loading: boolean
  failed: boolean
  loadingMessage: string
  errorMessage: string
  emptyMessage: string
  onDone?: () => void
}) {
  return (
    <Combobox.Portal>
      <Combobox.Positioner align="start" sideOffset={4} className="z-50" data-slot="picker-positioner">
        <Combobox.Popup
          data-slot="picker-popup"
          className={cn(
            'max-h-[min(20rem,var(--available-height))] w-[var(--anchor-width)] min-w-48 origin-[var(--transform-origin)] overflow-y-auto',
            'rounded-lg border border-border bg-popover p-1 text-popover-foreground shadow-lg outline-none',
            'max-[899px]:flex max-[899px]:flex-col',
            'transition-[opacity,transform] duration-(--duration-fast) data-[ending-style]:opacity-0 data-[starting-style]:scale-95 data-[starting-style]:opacity-0',
          )}
        >
          <div className="sticky top-0 z-10 mb-1 hidden items-center justify-between gap-2 border-b border-border bg-popover px-2 py-1 max-[899px]:flex">
            <span className="text-sm font-semibold">Options</span>
            <button
              type="button"
              data-slot="dialog-close"
              className="inline-flex h-11 cursor-pointer items-center rounded-md px-3 text-sm font-medium text-primary hover:bg-muted"
              onClick={onDone}
            >
              Done
            </button>
          </div>
          <Combobox.Status className="sr-only">{loading ? loadingMessage : null}</Combobox.Status>
          {loading ? (
            <div className="flex items-center gap-2 px-2 py-3 text-sm text-muted-foreground">
              <LoaderCircle aria-hidden className="size-4 animate-spin" />
              {loadingMessage}
            </div>
          ) : null}
          {failed ? <div className="px-2 py-3 text-center text-sm text-warning">{errorMessage}</div> : null}
          {!loading && !failed ? (
            <Combobox.Empty className="px-2 py-3 text-center text-sm text-muted-foreground">{emptyMessage}</Combobox.Empty>
          ) : null}
          <Combobox.List>
            {(group: PickerGroup) => (
              <Combobox.Group key={group.value || '__ungrouped'} items={group.items}>
                {group.value ? (
                  <Combobox.GroupLabel className="px-2 pt-2 pb-1 text-xs font-medium text-muted-foreground">
                    {group.value}
                  </Combobox.GroupLabel>
                ) : null}
                <Combobox.Collection>{(option: PickerOption) => <Item key={option.value} option={option} />}</Combobox.Collection>
              </Combobox.Group>
            )}
          </Combobox.List>
        </Combobox.Popup>
      </Combobox.Positioner>
    </Combobox.Portal>
  )
}

function InputActions({ clearable, clearLabel }: { clearable: boolean; clearLabel: string }) {
  return (
    <span className="flex shrink-0 items-center pr-1">
      {clearable ? (
        <Combobox.Clear
          aria-label={clearLabel}
          className="inline-flex size-7 items-center justify-center rounded text-muted-foreground hover:bg-muted hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring max-[899px]:size-11"
        >
          <X aria-hidden className="size-3.5" />
        </Combobox.Clear>
      ) : null}
      <Combobox.Trigger
        aria-label="Open options"
        className="inline-flex size-7 items-center justify-center rounded text-muted-foreground hover:bg-muted hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring max-[899px]:size-11"
      >
        <ChevronsUpDown aria-hidden className="size-4" />
      </Combobox.Trigger>
    </span>
  )
}

function PickerSingle(props: PickerSingleProps) {
  const [open, setOpen] = React.useState(false)
  const childOptions = React.useMemo(() => optionsFromChildren(props.children), [props.children])
  const options = props.options ?? childOptions
  const { setQuery, results, knownOptions, loading, failed } = useAsyncOptions(options, props.loadOptions)
  const groupedKnown = React.useMemo(() => groupOptions(knownOptions), [knownOptions])
  const groupedResults = React.useMemo(() => groupOptions(results), [results])
  const items = React.useMemo(
    () =>
      Combobox.createItems<PickerOption, string>(groupedKnown, { getValue: (option) => option.value, getLabel: (option) => option.label }),
    [groupedKnown],
  )
  const fieldRef = React.useRef<HTMLDivElement>(null)
  const ariaLabel = props.ariaLabel ?? props['aria-label']
  const clearable = props.clearable ?? options.some((option) => option.value === '')
  const emptyLabel = options.find((option) => option.value === '')?.label

  const change = (next: string | null) => {
    const value = next ?? ''
    setOpen(false)
    props.onValueChange?.(value)
    if (props.onChange) {
      const target = { value, name: props.name ?? '' } as HTMLSelectElement
      props.onChange({ target, currentTarget: target } as React.ChangeEvent<HTMLSelectElement>)
    }
  }

  return (
    <Combobox.Root<string, false, PickerOption>
      open={open}
      onOpenChange={(next) => {
        setOpen(next)
        if (next) revealPickerField(fieldRef.current)
      }}
      items={items}
      filteredItems={props.loadOptions ? groupedResults : undefined}
      filter={
        props.loadOptions ? null : (option, needle) => optionText(option).toLocaleLowerCase().includes(needle.trim().toLocaleLowerCase())
      }
      value={props.value || null}
      onValueChange={(next, details) => {
        // Typing into an already-empty controlled picker reports another empty
        // selection. Do not bounce that redundant value through the parent:
        // a parent render would replace the live query before async search runs.
        if (next === null && props.value === '' && details.reason === 'input-change') return
        change(next)
      }}
      onInputValueChange={(next, details) => {
        if (details.reason === 'input-change' || details.reason === 'input-clear') setQuery(next)
      }}
      autoHighlight
      disabled={props.disabled}
      required={props.required}
      name={props.name}
      id={props.id}
    >
      <div ref={fieldRef} data-slot="picker-input" className="max-[899px]:relative max-[899px]:z-[60]">
        <Combobox.InputGroup
          title={props.title}
          className={cn(
            'flex min-h-9 w-full items-center rounded-md border border-input bg-card text-sm outline-none',
            'max-[899px]:min-h-11',
            'focus-within:ring-2 focus-within:ring-ring data-[disabled]:opacity-50',
            props.className,
            // Callers migrated from native <select> pass `block`/`px-2`; the trigger must stay a flex row so its
            // clear and chevron buttons sit inside the box, not wrap below it.
            'flex px-0',
          )}
        >
          <Combobox.Input
            aria-label={ariaLabel}
            autoFocus={props.autoFocus}
            placeholder={props.placeholder ?? emptyLabel ?? 'Search or pick…'}
            className="min-w-0 flex-1 bg-transparent px-3 py-2 outline-none placeholder:text-muted-foreground"
            onFocus={(event) => event.currentTarget.select()}
            onClick={(event) => event.currentTarget.select()}
          />
          <InputActions
            clearable={clearable && Boolean(props.value)}
            clearLabel={props.clearLabel ?? `Clear ${ariaLabel ?? 'selection'}`}
          />
        </Combobox.InputGroup>
      </div>
      <List
        loading={loading}
        failed={failed}
        loadingMessage={props.loadingMessage ?? 'Loading options…'}
        errorMessage={props.errorMessage ?? 'Options unreachable'}
        emptyMessage={props.emptyMessage ?? 'Nothing matches'}
        onDone={() => setOpen(false)}
      />
    </Combobox.Root>
  )
}

function PickerMultiple(props: PickerMultipleProps) {
  const options = props.options ?? []
  const { setQuery, results, knownOptions, loading, failed } = useAsyncOptions(options, props.loadOptions)
  const groupedKnown = React.useMemo(() => groupOptions(knownOptions), [knownOptions])
  const groupedResults = React.useMemo(() => groupOptions(results), [results])
  const items = React.useMemo(
    () =>
      Combobox.createItems<PickerOption, string>(groupedKnown, { getValue: (option) => option.value, getLabel: (option) => option.label }),
    [groupedKnown],
  )
  const byValue = React.useMemo(() => new Map(knownOptions.map((option) => [option.value, option])), [knownOptions])
  const ariaLabel = props.ariaLabel ?? props['aria-label']
  const fieldRef = React.useRef<HTMLDivElement>(null)
  const [open, setOpen] = React.useState(false)

  return (
    <Combobox.Root<string, true, PickerOption>
      multiple
      open={open}
      items={items}
      filteredItems={props.loadOptions ? groupedResults : undefined}
      filter={
        props.loadOptions ? null : (option, needle) => optionText(option).toLocaleLowerCase().includes(needle.trim().toLocaleLowerCase())
      }
      value={props.value}
      onValueChange={props.onValueChange}
      onInputValueChange={(next, details) => {
        if (details.reason === 'input-change' || details.reason === 'input-clear') setQuery(next)
      }}
      onOpenChange={(next, details) => {
        if (!next && details.reason === 'item-press') {
          details.cancel()
          return
        }
        setOpen(next)
        if (next) revealPickerField(fieldRef.current)
      }}
      autoHighlight
      disabled={props.disabled}
      required={props.required}
      name={props.name}
      id={props.id}
    >
      <div ref={fieldRef} data-slot="picker-input" className="max-[899px]:relative max-[899px]:z-[60]">
        <Combobox.InputGroup
          title={props.title}
          className={cn(
            'flex min-h-9 w-full items-center rounded-md border border-input bg-card text-sm outline-none',
            'max-[899px]:min-h-11',
            'focus-within:ring-2 focus-within:ring-ring data-[disabled]:opacity-50',
            props.className,
            // Callers migrated from native <select> pass `block`/`px-2`; the trigger must stay a flex row so its
            // clear and chevron buttons sit inside the box, not wrap below it.
            'flex px-0',
          )}
        >
          <Combobox.Value>
            {(selected: string[]) => (
              <Combobox.Chips className="flex min-w-0 flex-1 flex-wrap items-center gap-1 px-2 py-1" aria-label="Selected options">
                {selected.map((value) => (
                  <Combobox.Chip
                    key={value}
                    aria-description="Press Backspace or Delete to remove"
                    className="inline-flex max-w-48 items-center gap-1 rounded bg-muted px-1.5 py-0.5 text-xs"
                  >
                    <span className="truncate">{byValue.get(value)?.label ?? value}</span>
                    <Combobox.ChipRemove
                      aria-label={`Remove ${byValue.get(value)?.label ?? value}`}
                      className="rounded hover:text-destructive"
                    >
                      <X aria-hidden className="size-3" />
                    </Combobox.ChipRemove>
                  </Combobox.Chip>
                ))}
                <Combobox.Input
                  aria-label={ariaLabel}
                  autoFocus={props.autoFocus}
                  aria-description={selected.length ? `${selected.length} selected. Use Left Arrow to reach selected options.` : undefined}
                  placeholder={selected.length ? '' : (props.placeholder ?? 'Search or pick…')}
                  className="min-w-24 flex-1 bg-transparent px-1 py-1 outline-none placeholder:text-muted-foreground"
                  onPointerUp={(event) => event.currentTarget.focus()}
                />
              </Combobox.Chips>
            )}
          </Combobox.Value>
          <InputActions
            clearable={(props.clearable ?? true) && props.value.length > 0}
            clearLabel={props.clearLabel ?? `Clear ${ariaLabel ?? 'selections'}`}
          />
        </Combobox.InputGroup>
      </div>
      <List
        loading={loading}
        failed={failed}
        loadingMessage={props.loadingMessage ?? 'Loading options…'}
        errorMessage={props.errorMessage ?? 'Options unreachable'}
        emptyMessage={props.emptyMessage ?? 'Nothing matches'}
        onDone={() => setOpen(false)}
      />
    </Combobox.Root>
  )
}

/** Shared searchable picker for single values, async results, and multi-select fields. */
export function Picker(props: PickerProps) {
  return props.multiple ? <PickerMultiple {...props} /> : <PickerSingle {...props} />
}
